import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Search, TrendingUp, Users, Star, Plus, Filter } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

// Removed page-load variants; keep hover scale only

interface QuickActionsProps {
  hasInfluencers?: boolean
}

export function QuickActions({ hasInfluencers = false }: QuickActionsProps) {
  const navigate = useNavigate()

  const quickActions = [
    {
      title: "Find New Accounts",
      description: "Search and discover new creators",
      icon: Search,
      gradient: "from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20",
      iconColor: "text-blue-600 dark:text-blue-400",
      action: () => navigate('/search'),
      primary: true
    },
    {
      title: "Request Analytics",
      description: "Get detailed creator insights",
      icon: TrendingUp,
      gradient: "from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20",
      iconColor: "text-green-600 dark:text-green-400", 
      action: () => navigate('/request-analytics'),
      primary: false
    },
    {
      title: "Create Watchlist",
      description: "Monitor creators over time",
      icon: Star,
      gradient: "from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20",
      iconColor: "text-purple-600 dark:text-purple-400",
      action: () => navigate('/watchlists'),
      primary: false
    }
  ]

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-muted-foreground" />
            Quick Actions
          </CardTitle>
          <CardDescription>
            {hasInfluencers 
              ? "Discover more creators or analyze your saved ones" 
              : "Get started by finding and saving accounts"
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quickActions.map((action, index) => (
              <motion.div
                key={action.title}
                whileHover={{ scale: 1.02, y: -2 }}
                className="cursor-pointer"
                onClick={action.action}
              >
                <Card className={`relative overflow-hidden border-0 bg-gradient-to-br ${action.gradient} hover:shadow-md transition-all duration-200`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg bg-white/50 dark:bg-black/10`}>
                        <action.icon className={`h-5 w-5 ${action.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-foreground mb-1">
                          {action.title}
                        </h3>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {action.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
} 